import { findAndReplace, type Replace } from "mdast-util-find-and-replace";
import type { Root } from "mdast";
import { visit } from "unist-util-visit";

interface RemarkMentionOptions {
  tags?: string[];
}

/**
 * Remark plugin that transforms @username patterns into clickable mention nodes,
 * but only for usernames listed in the `tags` array.
 *
 * Uses a non-word boundary via capture group instead of lookbehind for Safari < 16.4 compat.
 */
export default function remarkMention(options: RemarkMentionOptions = {}) {
  return (tree: Root) => {
    const { tags } = options;
    if (!tags?.length) {
      return;
    }

    const escaped = [...tags]
      .sort((a, b) => b.length - a.length)
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const pattern = new RegExp(`(^|[^\\w])@(${escaped.join("|")})(?!\\w)`, "g");

    findAndReplace(tree, [[pattern, replaceMention as unknown as Replace]]);

    // Remove mention nodes that ended up inside links (invalid nested interactive HTML).
    visit(tree, "link", (node) => {
      visit(node, "mention", (_mention, index, parent) => {
        if (parent && index != null) {
          const text = (_mention as MentionNode).children[0]?.value;
          if (text) {
            (parent as { children: unknown[] }).children[index] = {
              type: "text",
              value: text,
            };
          }
        }
      });
    });
  };
}

function makeMentionNode(username: string) {
  return {
    type: "mention" as const,
    data: {
      hName: "span",
      hProperties: { className: ["mention"], "data-username": username },
    },
    children: [{ type: "text" as const, value: `@${username}` }],
  };
}

type MentionNode = ReturnType<typeof makeMentionNode>;

function replaceMention(_match: string, prefix: string, username: string) {
  const mention = makeMentionNode(username);
  if (prefix) {
    return [{ type: "text" as const, value: prefix }, mention];
  }
  return mention;
}
